import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBjb25jcmV0ZSUyMGdsYXNzJTIwbWluaW1hbGlzdCUyMGZhY2FkZXxlbnwwfHx8fDE3OTA4MjU5MDV8MA&ixlib=rb-4.1.0&q=85";
const PORTRAIT = "/assets/portrait-suit.webp";

const LINES = ["Nieruchomości.", "Ekspansja.", "Inwestycje."];
const STATS = [
  { value: "20+", label: "lat doświadczenia w biznesie" },
  { value: "2", label: "lata jako burmistrz miasta i gminy" },
  { value: "4", label: "województwa Polski południowo-wschodniej" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section
      id="start"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#090D16]"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={HERO_IMG} alt="" aria-hidden="true" className="h-[125%] w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090D16]/80 via-[#090D16]/60 to-[#090D16]/95" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-10 pt-32 sm:px-8 lg:min-h-screen lg:grid-cols-12 lg:gap-16 lg:px-16 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-[#D8B07C]"
          >
            Janusz Błotnicki — Investment &amp; Expansion Partner
          </motion.p>

          <h1 data-testid="hero-headline" className="max-w-2xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block font-serif text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span
                className="block pt-3 font-serif text-xl italic text-[#D8B07C] sm:text-2xl"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                — od lokalizacji do realizacji.
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-white/75 sm:text-base"
          >
            Pomagam inwestorom, deweloperom i sieciom handlowo-usługowym znajdować odpowiednie
            nieruchomości, oceniać ich potencjał i przeprowadzać projekty inwestycyjne — od pierwszej
            analizy do realizacji.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9"
          >
            <a
              href="#kontakt"
              data-testid="hero-contact-button"
              className="group inline-flex items-center gap-3 rounded-full bg-[#8C6D46] px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#735733]"
            >
              Porozmawiajmy o inwestycji
              <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative mx-auto max-w-sm lg:max-w-none">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-sm border border-[#D8B07C]/40" />
            <img
              src={PORTRAIT}
              alt="Janusz Błotnicki — Investment & Expansion Partner"
              data-testid="hero-portrait-image"
              className="relative aspect-[4/5] w-full rounded-sm object-cover object-top"
            />
            <div className="absolute -bottom-6 -right-4 bg-[#8C6D46] px-6 py-4 sm:-right-6">
              <p className="font-serif text-base italic leading-tight text-white sm:text-lg">
                „Od lokalizacji<br />do realizacji."
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        data-testid="hero-stats"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 border-t border-white/15 px-4 py-8 sm:grid-cols-3 sm:px-8 lg:px-16"
      >
        {STATS.map((s) => (
          <div key={s.label} className="flex items-baseline gap-3">
            <span className="font-serif text-4xl font-medium text-white">{s.value}</span>
            <span className="text-xs uppercase tracking-[0.15em] text-white/55">{s.label}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
};
